import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Printer, MessageCircle, X, Check, Bluetooth } from "lucide-react";
import { openBillWindow, shareBillWhatsApp } from "../lib/bill";
import { printBill as btPrint, getSavedPrinter } from "../lib/bluetoothPrinter";
import { fmtINR, fmtNum, fmtDate } from "../lib/api";
import { toast } from "sonner";

export default function BillShareModal({ open, onClose, sale, defaultPhone = "" }) {
  const [phone, setPhone] = useState(defaultPhone);

  if (!sale) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md rounded-none border-zinc-900" data-testid="bill-share-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Check className="h-5 w-5 text-emerald-600" />
            {sale.channel === "wholesale" ? "Invoice Saved" : "Bill Saved"}
          </DialogTitle>
        </DialogHeader>

        <div className="border border-zinc-200 p-4 bg-zinc-50">
          <div className="flex justify-between text-xs uppercase tracking-wider text-zinc-500 mb-2">
            <span>Bill #{sale.bill_no || sale.id?.slice(0, 8).toUpperCase()}</span>
            <span>{fmtDate(sale.date)}</span>
          </div>
          {sale.customer_name && (
            <div className="text-sm mb-2">
              Customer: <b>{sale.customer_name}</b>
            </div>
          )}
          <div className="border-t border-zinc-200 pt-2 space-y-1">
            {sale.items.map((it, i) => {
              const qty = it.unit === "piece"
                ? `${Math.round(Number(it.kg || 0))} pcs`
                : `${fmtNum(it.kg)} kg`;
              return (
                <div key={i} className="flex justify-between text-xs">
                  <span>{it.product_name}</span>
                  <span className="font-mono-num">
                    {qty} × ₹{fmtNum(it.rate)} = {fmtINR(it.amount)}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between items-baseline mt-3 pt-3 border-t border-zinc-200">
            <span className="text-xs uppercase tracking-wider text-zinc-500">Total</span>
            <span className="font-display text-2xl font-bold font-mono-num">
              {fmtINR(sale.total_amount)}
            </span>
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">
            Customer WhatsApp (optional, with country code)
          </label>
          <input
            type="tel"
            placeholder="e.g. 919876543210"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="bill-phone-input"
            className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-sky-600"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => openBillWindow(sale)}
            data-testid="bill-print-btn"
            className="border border-zinc-900 px-4 py-3 text-sm uppercase tracking-wider hover:bg-zinc-100 flex items-center justify-center gap-2"
          >
            <Printer className="h-4 w-4" />
            {sale.channel === "wholesale" ? "Print A4 Invoice" : "Print Receipt"}
          </button>
          <button
            onClick={() => shareBillWhatsApp(sale, { phone })}
            data-testid="bill-whatsapp-btn"
            className="bg-emerald-600 text-white px-4 py-3 text-sm uppercase tracking-wider hover:bg-emerald-700 flex items-center justify-center gap-2"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </button>
        </div>
        <button
          onClick={async () => {
            try {
              toast.info(getSavedPrinter() ? "Sending to printer…" : "Choose your Bluetooth printer…");
              await btPrint(sale);
              toast.success("Bill sent to thermal printer");
            } catch (e) {
              toast.error(e.message || "Bluetooth print failed");
            }
          }}
          data-testid="bill-bt-print-btn"
          className="w-full border border-sky-600 text-sky-700 hover:bg-sky-50 px-4 py-2.5 text-sm uppercase tracking-wider flex items-center justify-center gap-2"
        >
          <Bluetooth className="h-4 w-4" />
          Print to Bluetooth Thermal Printer
        </button>
        <button
          onClick={onClose}
          data-testid="bill-close-btn"
          className="w-full border border-zinc-300 px-4 py-2 text-xs uppercase tracking-wider hover:bg-zinc-100"
        >
          Done · New Bill
        </button>
      </DialogContent>
    </Dialog>
  );
}
